import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import { AlertCircle, ArrowLeft, LogIn } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/error/no-organization")({
  component: NoOrganizationError,
});

/**
 * Error page shown when user has no organization assigned.
 *
 * This can happen due to configuration issues where:
 * - User webhook fired but organization webhooks didn't
 * - Organization sync failed
 * - User was removed from all organizations
 *
 * This page is shown in a standalone layout (no app header/navigation)
 * to make it clear this is a critical error state, not a normal loading
 * or empty state.
 */
function NoOrganizationError() {
  const { signOut } = useAuth();

  const handleSignInAgain = () => {
    // Sign out and redirect to login page
    signOut({ returnTo: `${window.location.origin}/auth/login` });
  };

  return (
    <div className="w-full max-w-md px-4">
      <Card className="p-8">
        <CardHeader className="space-y-2 pb-6 px-0">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="size-5 text-muted-foreground" />
            <CardTitle className="text-2xl">No Organization Access</CardTitle>
          </div>
          <CardDescription>You don't belong to any organization in the system</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-0 px-0 pb-0">
          {/* Alert Box */}
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>Access Required</AlertTitle>
            <AlertDescription>
              To use this application, you must be a member of at least one organization. This
              usually happens automatically during setup, but there may have been a configuration
              issue.
            </AlertDescription>
          </Alert>

          {/* Instructions */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">What to do next:</h4>
            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
              <li>Contact your system administrator</li>
              <li>Verify your account has been properly set up</li>
              <li>Ask to be added to an organization</li>
            </ol>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-2">
            <Button variant="default" className="w-full" onClick={handleSignInAgain}>
              <LogIn className="size-4 mr-2" />
              Sign In Again
            </Button>

            <Link to="/" className="w-full">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="size-4 mr-2" />
                Return to Home
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
